// components/modals/ClusterOverviewModal.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, IconButton, Divider, Chip, useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import ProposeSessionModal from './ProposeSessionModal';
import axios from 'axios';

const ClusterOverviewModal = ({ cluster, onClose, onExpand }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [proposeOpen, setProposeOpen] = useState(false);
    const [timeSlotInfo, setTimeSlotInfo] = useState({ isLoading: true, data: null });

    useEffect(() => {
        if (!cluster || !cluster._id) {
            setTimeSlotInfo({ isLoading: false, data: null });
            return;
        }
        setTimeSlotInfo({ isLoading: true, data: null });
        const fetchTopTimeSlot = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clusters/${cluster._id}/top-timeslot`);
                setTimeSlotInfo({ isLoading: false, data: response.data });
            } catch (error) {
                console.error('Failed to fetch top time slot:', error);
                setTimeSlotInfo({ isLoading: false, data: { error: 'Failed to load data' } });
            }
        };
        fetchTopTimeSlot();
    }, [cluster]);

    const renderTimeSlotInfo = () => {
        if (timeSlotInfo.isLoading) {
            return <Typography variant="body2"><strong>Top Time Slot:</strong> Calculating...</Typography>;
        }
        if (!timeSlotInfo.data || timeSlotInfo.data.error) {
            return <Typography variant="body2" sx={{ color: '#ff9800' }}><strong>Top Time Slot:</strong> Error loading</Typography>;
        }
        const { topTimeSlot, totalPreferencesAnalyzed, totalProspectsInCluster } = timeSlotInfo.data;
        let displayText;
        if (totalPreferencesAnalyzed > 0) {
            displayText = topTimeSlot;
        } else if (totalProspectsInCluster > 0) {
            displayText = `None submitted (${totalProspectsInCluster} prospects)`;
        } else {
            displayText = "No prospects found";
        }
        return (
            <Typography variant="body2">
                <strong>Top Time Slot:</strong> {displayText}
            </Typography>
        );
    };

    if (!cluster) return null;

    const activationThreshold = 30;
    const leadsNeeded = activationThreshold - (cluster.leadCount || 0);
    const isReady = cluster.leadCount >= activationThreshold;

    // ✅ FIX 1: Create a handler that stops the click from propagating to the map.
    const handleClose = (e) => {
        e.stopPropagation();
        onClose();
    };

    return (
        <>
            <Box
                // ✅ FIX 2: Stop clicks on the modal background from reaching the map.
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: 'absolute',
                    top: isMobile ? '10%' : '20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#202020',
                    padding: '1.5rem',
                    borderRadius: 2,
                    boxShadow: 5,
                    color: '#fff',
                    zIndex: 2000,
                    width: isMobile ? '90vw' : 380,
                    maxWidth: '95vw',
                    border: '1px solid #333',
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 'bold', color: '#12c099' }}>
                        {cluster.cityName || cluster.name}
                    </Typography>
                    {/* ✅ FIX 3: Use the new handleClose function for the close button. */}
                    <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {/* --- The rest of your component is completely unchanged. --- */}
                <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                    Zip Code: {cluster.zipCodes?.[0] || 'N/A'}
                </Typography>
                {isReady ? (
                    <Chip label="Ready to Activate" size="small" sx={{ bgcolor: '#1b5e20', color: '#fff', mb: 2 }} />
                ) : (
                    <Typography variant="body2" sx={{ color: '#f44336', mb: 2 }}>
                        🚧 Need {leadsNeeded} more lead{leadsNeeded !== 1 ? 's' : ''} before activation
                    </Typography>
                )}
                <Typography variant="body2"><strong>Leads:</strong> {cluster.leadCount}</Typography>
                <Typography variant="body2"><strong>AI Score:</strong> {cluster.activationScore ? `${cluster.activationScore}%` : '93%'}</Typography>
                <Typography variant="body2"><strong>Affluence (Local):</strong> {cluster.affluenceScore}</Typography>
                <Typography variant="body2"><strong>Affluence (Global):</strong> {cluster.globalAffluenceScore}</Typography>

                {renderTimeSlotInfo()}

                <Divider sx={{ my: 2, borderColor: '#444' }} />

                <Box mt={1} display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1} justifyContent={'space-between'} alignItems="center">
                    <Button variant="outlined" fullWidth={isMobile} onClick={() => setProposeOpen(true)} sx={{ color: '#12c099', borderColor: '#12c099' }}>
                        Propose Session
                    </Button>
                    <Button variant="text" fullWidth={isMobile} onClick={onExpand} sx={{ color: '#12c099' }}>
                        Expand Details
                    </Button>
                </Box>
            </Box>

            <ProposeSessionModal open={proposeOpen} onClose={() => setProposeOpen(false)} />
        </>
    );
};

export default ClusterOverviewModal;
